import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  FlaskConical,
  Loader2,
} from 'lucide-react';

interface OtpStepProps {
  sentTo: string;
  expiresIn: number;
  isVerifying: boolean;
  isSuccess: boolean;
  errorMessage: string | null;
  devOtp?: string | null;
  backLabel?: string;
  onBack: () => void;
  onSubmit: (code: string) => void;
  onResend: () => Promise<void> | void;
}

const OTP_LENGTH = 6;

const OtpStep: React.FC<OtpStepProps> = ({
  sentTo,
  expiresIn,
  isVerifying,
  isSuccess,
  errorMessage,
  devOtp,
  backLabel,
  onBack,
  onSubmit,
  onResend,
}) => {
  const { t } = useTranslation();

  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(''));
  const [countdown, setCountdown] = useState<number>(expiresIn);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setCountdown(expiresIn);
  }, [expiresIn]);

  useEffect(() => {
    if (countdown <= 0) return;
    const id = window.setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => window.clearInterval(id);
  }, [countdown]);

  const code = useMemo(() => digits.join(''), [digits]);
  const canSubmit = code.length === OTP_LENGTH && !isVerifying && !isSuccess;
  const canResend = countdown <= 0 && !isResending && !isSuccess;

  useEffect(() => {
    if (canSubmit) {
      onSubmit(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleDigit = (idx: number, value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (cleaned.length > 1) {
      setDigits((prev) => {
        const next = [...prev];
        cleaned.split('').forEach((digit, offset) => {
          if (idx + offset < OTP_LENGTH) next[idx + offset] = digit;
        });
        return next;
      });
      inputRefs.current[Math.min(idx + cleaned.length, OTP_LENGTH - 1)]?.focus();
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = cleaned;
      return next;
    });
    if (cleaned && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => {
      next[i] = ch;
    });
    setDigits(next);
    const lastIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[lastIdx]?.focus();
  };

  const handleAutofill = () => {
    if (!devOtp) return;
    const cleaned = devOtp.replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (cleaned.length !== OTP_LENGTH) return;
    setDigits(cleaned.split(''));
    inputRefs.current[OTP_LENGTH - 1]?.focus();
  };

  const handleResend = async () => {
    if (!canResend) return;
    try {
      setIsResending(true);
      await onResend();
    } finally {
      setIsResending(false);
    }
  };

  const minutes = Math.floor(countdown / 60);
  const seconds = (countdown % 60).toString().padStart(2, '0');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
          {t('courierAuth.otp.heading')}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          {t('courierAuth.otp.sentTo', { phone: sentTo })}
        </p>
      </div>

      {devOtp && !isSuccess && (
        <div className="flex items-center gap-3 border-s-2 border-amber-500 bg-amber-50 px-4 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-200/70 text-amber-800">
            <FlaskConical className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
              DEV / SMS stub mode
            </div>
            <div
              className="font-mono text-lg font-bold text-amber-900 tracking-[0.35em]"
              dir="ltr"
            >
              {devOtp}
            </div>
          </div>
          <button
            type="button"
            onClick={handleAutofill}
            disabled={isVerifying}
            className="shrink-0 rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            Autofill
          </button>
        </div>
      )}

      <div className="flex justify-center gap-1.5 sm:gap-2" dir="ltr">
        {digits.map((digit, idx) => (
          <input
            key={idx}
            ref={(el) => {
              inputRefs.current[idx] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={idx === 0 ? 'one-time-code' : 'off'}
            maxLength={idx === 0 ? OTP_LENGTH : 1}
            value={digit}
            disabled={isVerifying || isSuccess}
            onChange={(e) => handleDigit(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            onPaste={handlePaste}
            className={`h-12 w-10 rounded-lg border text-center text-xl font-bold outline-none transition-colors focus:ring-2 focus:ring-offset-1 sm:h-14 sm:w-12 sm:rounded-xl sm:text-2xl ${
              isSuccess
                ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                : digit
                  ? 'border-[#bf0035] bg-white text-gray-900'
                  : 'border-gray-300 bg-white focus:border-[#bf0035] focus:ring-[#bf0035]/15'
            }`}
          />
        ))}
      </div>

      {errorMessage && !isSuccess && (
        <div className="border-s-2 border-red-600 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      {isSuccess && (
        <div className="flex items-center gap-2 font-semibold text-emerald-700">
          <CheckCircle2 className="h-5 w-5" />
          {t('courierAuth.otp.success')}
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={handleResend}
          disabled={!canResend}
          className="font-semibold text-haat-red-600 hover:text-haat-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {isResending ? t('common.loading') : t('courierAuth.otp.resend')}
        </button>
        <span className="text-gray-500 tabular-nums">
          {countdown > 0 ? `${minutes}:${seconds}` : t('courierAuth.otp.expired')}
        </span>
      </div>

      <button
        type="button"
        onClick={onBack}
        disabled={isVerifying || isSuccess}
        className="btn-outline w-full disabled:opacity-50"
      >
        {backLabel ?? t('courierAuth.otp.changeChannel')}
      </button>

      {isVerifying && !isSuccess && (
        <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('courierAuth.otp.verifying')}
        </div>
      )}
    </div>
  );
};

export default OtpStep;
